import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router";
import { Button, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import theme from "styles/theme";
import type { IStore } from "reducers";
import { toggleCartInsightsSidePanel, toggleGalleryInsightsSidePanel } from "actions/insights-actions";
import InsightsIcon from "components/icons/insights";
import { redirectWithWarning } from "actions/warning-actions";
import { isCurrencyCodeUSDSelector, isInsightsButtonEnabledSelector } from "reducers/insights-reducer";
import { useApp } from "../../../hooks/useApp";
import { fetchProductStyleInsights } from "actions/insights/product-style-insights";
import Globalize from "globalize";
import { InsightsActions } from "constants/analytics";
import { DATATEST } from "constants/integration";
import { fetchCategoryInsights } from "actions/insights/category-insights";
import { fetchProductColorInsights } from "actions/insights/product-color-insights";
import { fetchProductSizeInsights } from "actions/insights/product-size-insights";
import type { IProductSizeInsights } from "types/insights/product-size-insight";

type Page = "cart" | "gallery" | "other";

const isItemPage = (pathname: string) => pathname.includes("/item");

const getPage = (pathname): Page => {
    if (pathname.includes("cart")) {
        return "cart";
    } else if (pathname.includes("gallery")) {
        return "gallery";
    } else {
        return "other";
    }
};

interface StateProps {
    isOrderInsightsEnabled: boolean;
    isCartInsightsSidePanelOpen: boolean;
    isGalleryInsightsSidePanelOpen: boolean;
    isNewPDPForOrderInsightsEnabled: boolean;
    isButtonEnabled: boolean;
    isCurrencyUSD: boolean;
    productInsightsNu67199: boolean;
    categoryInsightsNu67199: boolean;
    colorInsightsNu67199: boolean;
    sizeInsightsNU67199: boolean;
    cart: IStore["cart"];
    sizeInsights: IProductSizeInsights;
    scope: IStore["scope"];
}

interface DispatchProps {
    onToggleCartSidePanel: () => void;
    onToggleGallerySidePanel: () => void;
    onClosePDPDrawer: () => void;
    onFetchProductStyleInsights: (viewAsCompanyId?: string) => void;
    onFetchCategoryInsights: (viewAsCompanyId?: string) => void;
    onFetchProductColorInsights: (viewAsCompanyId?: string) => void;
    onFetchProductSizeInsights: (productIds) => void;
}

interface OwnProps {}

interface OwnPropsWithRouter extends OwnProps, RouteComponentProps<any, any> {}

const useStyles = makeStyles({
    tooltip: {
        backgroundColor: "#2D2D2D",
        color: "#F0F2F2",
        width: "234px",
        fontSize: "12px",
        padding: "4px 8px",
        borderRadius: "4px",
    },
    button: {
        borderRadius: "2px !important",
        width: "44px",
        minWidth: "44px",
        height: "40px",
        padding: 0,
    },
});

/**
 * The button to toggle the insights side panel is used in the site header.
 * It is only shown when the order insights feature flag is enabled.
 * The button toggles the insights side panel for the current page and should be visible only for the cart and gallery pages.
 */
const InsightsSidePanelToggleButton: React.FC<OwnPropsWithRouter & StateProps & DispatchProps> = ({
    isOrderInsightsEnabled,
    isCartInsightsSidePanelOpen,
    isGalleryInsightsSidePanelOpen,
    onToggleCartSidePanel,
    onToggleGallerySidePanel,
    location,
    onClosePDPDrawer,
    isNewPDPForOrderInsightsEnabled,
    isButtonEnabled,
    isCurrencyUSD,
    onFetchProductStyleInsights,
    productInsightsNu67199,
    categoryInsightsNu67199,
    colorInsightsNu67199,
    sizeInsightsNU67199,
    cart,
    sizeInsights,
    onFetchCategoryInsights,
    onFetchProductColorInsights,
    onFetchProductSizeInsights,
    scope,
}) => {
    const classes = useStyles();
    const { me } = useApp();

    const insightsFetchingAllowed = React.useMemo(() => {
        return isOrderInsightsEnabled && (me.hasPermission("order_trends") || me.isNuOrderUser()) && isCurrencyUSD;
    }, [isOrderInsightsEnabled, me, isCurrencyUSD]);

    const cartItemIds = React.useMemo(() => {
        return cart.items ? (Array.from(cart.items.keys()) as string[]) : [];
    }, [cart.items]);

    React.useEffect(() => {
        if (insightsFetchingAllowed && productInsightsNu67199) {
            onFetchProductStyleInsights(scope.viewAs?._id);
        }
    }, [insightsFetchingAllowed, productInsightsNu67199, scope.viewAs]);

    React.useEffect(() => {
        if (insightsFetchingAllowed && categoryInsightsNu67199) {
            onFetchCategoryInsights(scope.viewAs?._id);
        }
    }, [insightsFetchingAllowed, categoryInsightsNu67199, scope.viewAs]);

    React.useEffect(() => {
        if (insightsFetchingAllowed && colorInsightsNu67199) {
            onFetchProductColorInsights(scope.viewAs?._id);
        }
    }, [insightsFetchingAllowed, colorInsightsNu67199, scope.viewAs]);

    React.useEffect(() => {
        const fetchedProductIds = sizeInsights.map((insight) => insight.productId) as string[];
        const cartHasUnfetchedItems = cartItemIds.some((id) => !fetchedProductIds.includes(id));
        if (
            insightsFetchingAllowed &&
            sizeInsightsNU67199 &&
            getPage(location.pathname) === "cart" &&
            cartHasUnfetchedItems
        ) {
            onFetchProductSizeInsights(cartItemIds);
        }
    }, [insightsFetchingAllowed, sizeInsightsNU67199, location.pathname, cart.items]);

    if (isOrderInsightsEnabled && (me.hasPermission("order_trends") || me.isNuOrderUser())) {
        const page = getPage(location.pathname);

        let isInsightsSidePanelOpen = false;
        if (page === "cart") {
            isInsightsSidePanelOpen = isCartInsightsSidePanelOpen;
        } else if (page === "gallery") {
            isInsightsSidePanelOpen = isGalleryInsightsSidePanelOpen;
        }

        const onToggleInsightsSidePanel = () => {
            if (page === "cart") {
                if (!isInsightsSidePanelOpen) {
                    global.analytics.track(InsightsActions.openDrawerCart);
                }
                onToggleCartSidePanel();
            } else if (page === "gallery") {
                if (!isInsightsSidePanelOpen) {
                    global.analytics.track(InsightsActions.openDrawerGallery);
                }
                onToggleGallerySidePanel();
                if (isItemPage(location.pathname) && isNewPDPForOrderInsightsEnabled) {
                    // if the feature flag for the new PDP for order insights is enabled
                    // Then close the PDP drawer
                    onClosePDPDrawer();
                }
            }
        };

        let tooltipText = "";
        if (!isCurrencyUSD) {
            tooltipText = Globalize.formatMessage("Order Trends are available in USD only");
        } else if (!isButtonEnabled) {
            tooltipText = Globalize.formatMessage(
                "Order Trends are only available for brands with sufficient data in USD orders."
            );
        }

        return (
            <Tooltip title={tooltipText} placement="bottom" classes={{ tooltip: classes.tooltip }}>
                <span>
                    <Button
                        className={classes.button}
                        onClick={onToggleInsightsSidePanel}
                        disabled={!isButtonEnabled}
                        data-test={DATATEST.INSIGHTS.TOGGLE_INSIGHTS_BUTTON}
                    >
                        <InsightsIcon
                            fontSize="large"
                            htmlColor={
                                isButtonEnabled && isInsightsSidePanelOpen
                                    ? theme.palette.primary1Color
                                    : theme.palette.grey
                            }
                        />
                    </Button>
                </span>
            </Tooltip>
        );
    } else {
        return null;
    }
};

const mapStateToProps = (state: IStore) => ({
    isCartInsightsSidePanelOpen: state.insights.cartInsightsSidePanelOpen,
    isGalleryInsightsSidePanelOpen: state.insights.galleryInsightsSidePanelOpen,
    isOrderInsightsEnabled: state.ldFlags.orderInsightsNu64953,
    isNewPDPForOrderInsightsEnabled: state.ldFlags.newPdPdrawerForOrderInsightsNu67228,
    isButtonEnabled: isInsightsButtonEnabledSelector(state),
    isCurrencyUSD: isCurrencyCodeUSDSelector(state),
    productInsightsNu67199: state.ldFlags.productInsightsNu67199,
    categoryInsightsNu67199: state.ldFlags.categoryInsightsNu67199,
    colorInsightsNu67199: state.ldFlags.colorInsightsNu67199,
    sizeInsightsNU67199: state.ldFlags.sizeInsightsNu67199,
    sizeInsights: state.insights.productSizes,
    cart: state.cart,
    scope: state.scope,
});

const mapDispatchToProps = (dispatch, ownProps: OwnPropsWithRouter) => ({
    onToggleCartSidePanel: () => dispatch(toggleCartInsightsSidePanel()),
    onToggleGallerySidePanel: () => dispatch(toggleGalleryInsightsSidePanel()),
    onFetchProductStyleInsights: (companyId) => dispatch(fetchProductStyleInsights(companyId)),
    onClosePDPDrawer: () => {
        const { location } = ownProps;
        dispatch(
            redirectWithWarning({
                pathname: _(location.pathname).split("/").slice(0, -2).join("/"),
                query: location.query && _.omit(location.query, ["prebook", "shipStart", "shipEnd", "orderType"]),
            })
        );
    },
    onFetchCategoryInsights: (companyId) => dispatch(fetchCategoryInsights(companyId)),
    onFetchProductColorInsights: (companyId) => dispatch(fetchProductColorInsights(companyId)),
    onFetchProductSizeInsights: (productIds) => dispatch(fetchProductSizeInsights(productIds)),
});

export default withRouter<OwnProps>(
    connect<StateProps, DispatchProps, OwnPropsWithRouter>(
        mapStateToProps,
        mapDispatchToProps
    )(InsightsSidePanelToggleButton)
);
